import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import type { PageDimensionsByPage } from '@heidi/templates/contracts';
import { DEFAULT_PAGE_DIMENSIONS } from '@heidi/templates/data';
import { loadPdfjs } from '@heidi/utils';

type PdfSourceKind = 'file' | 'arrayBuffer' | 'url';

const describeSourceKind = (source: File | ArrayBuffer | string): PdfSourceKind => {
  if (source instanceof File) return 'file';
  if (source instanceof ArrayBuffer) return 'arrayBuffer';
  return 'url';
};

/**
 * pdf.js failures carry the document's bytes and its URL in their messages, and
 * a template PDF is clinical content. Only a classification leaves this module.
 */
const classifyPdfError = (error: unknown) => ({
  error: sanitizeError(error),
  errorName: error instanceof Error ? error.name : typeof error,
});

type Pdfjs = Awaited<ReturnType<typeof loadPdfjs>>;
type PdfLoadingTask = ReturnType<Pdfjs['getDocument']>;
type PdfDocument = Awaited<PdfLoadingTask['promise']>;

/**
 * Reads PDF page dimensions (in PDF points) from a PDF source.
 *
 * Uses `pdf.js` viewport geometry (the same renderer as the UI) and returns
 * a map keyed by 1-based page number. Falls back to page 1 default US Letter
 * dimensions if parsing fails or the document has no pages.
 *
 * Stays app-side: `@heidi/templates/data` is headless and may not reach
 * `next/*` or the app's `next/dynamic` pdf.js loader. The pure converters this
 * reader feeds live behind that door.
 *
 * @param source - A `File` object, an `ArrayBuffer`, or a URL string pointing to a PDF.
 */
export async function getPdfPageDimensions(
  source: File | ArrayBuffer | string
): Promise<PageDimensionsByPage> {
  let loadingTask: PdfLoadingTask | null = null;
  let pdfDoc: PdfDocument | null = null;

  try {
    let data: ArrayBuffer;
    if (source instanceof File) {
      data = await source.arrayBuffer();
    } else if (source instanceof ArrayBuffer) {
      data = source;
    } else {
      const response = await fetch(source);
      data = await response.arrayBuffer();
    }

    const pdfjs = await loadPdfjs();

    loadingTask = pdfjs.getDocument({ data: new Uint8Array(data) });
    pdfDoc = await loadingTask.promise;

    if (pdfDoc.numPages === 0) {
      logger.debug('[PDF] No pages found, using default dimensions map');
      return { 1: DEFAULT_PAGE_DIMENSIONS };
    }

    const pdfDocument = pdfDoc;
    const pageNumbers = Array.from(
      { length: pdfDocument.numPages },
      (_, index) => index + 1
    );
    const pages = await Promise.all(
      pageNumbers.map((pageNumber) => pdfDocument.getPage(pageNumber))
    );

    const pageDimensionsByPage: PageDimensionsByPage = {};
    pages.forEach((page) => {
      const pageNumber = page.pageNumber;
      const viewport = page.getViewport({ scale: 1 });

      pageDimensionsByPage[pageNumber] = {
        width: viewport.width,
        height: viewport.height,
      };
    });

    logger.debug('[PDF] Page dimensions detected by page', {
      pages: Object.keys(pageDimensionsByPage).length,
    });

    return pageDimensionsByPage;
  } catch (error) {
    logger.warn('[PDF] Failed to read page dimensions by page, using defaults', {
      ...classifyPdfError(error),
      sourceKind: describeSourceKind(source),
    });
    return { 1: DEFAULT_PAGE_DIMENSIONS };
  } finally {
    try {
      await pdfDoc?.destroy();
    } catch (cleanupError) {
      logger.warn(
        '[PDF] Failed to destroy PDF document during cleanup',
        classifyPdfError(cleanupError)
      );
    }

    try {
      await loadingTask?.destroy();
    } catch (cleanupError) {
      logger.warn(
        '[PDF] Failed to destroy PDF loading task during cleanup',
        classifyPdfError(cleanupError)
      );
    }
  }
}
